import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/queryKeys';
import { getDataset, setRuntimeDataset } from '../datasets';
import { MY_TEAM_DATASET_CODE } from '../datasets/myTeam';
import { enablePreviewMock } from '../mock/previewAdapter';
import { armPreview, getPreviewDatasetCode, isPreviewArmed } from '../mock/previewMode';
import { fetchMyTeamDataset } from './myTeamFetch';
import { buildGroups, buildOrg, buildUser, clearIntent, readIntent } from './previewIdentity';

// Runs once in index.tsx, AFTER the module-level queryClient is constructed and BEFORE root.render
// (awaited). `primeArmedFromIntent()` must already have run at the very top of index.tsx (so the
// egress kill-switch suppresses telemetry before PostHog/Analytics init). With no intent this is a
// no-op and the app boots normally.
//
// Two arming paths:
//  • Synthetic sandbox (aviation, …): already armed by primeArmedFromIntent → just seed.
//  • "Show my team": NOT armed yet. We do the one real read HERE, while un-armed (so it's a normal
//    authenticated request and the fail-closed mock is never holed — ADR 0001 Amendment 1), build
//    the dataset, register it, then arm. On failure we leave the session un-armed (the normal app)
//    rather than show a broken demo.
//
// Order matters: register the fail-closed dispatcher BEFORE seeding — so any refetch that fires the
// instant a component mounts is already served (never reaches the real network).
export async function bootstrapPreview(queryClient: QueryClient): Promise<void> {
  const intent = readIntent();
  if (!intent) return;

  if (intent.datasetCode === MY_TEAM_DATASET_CODE && !isPreviewArmed()) {
    try {
      setRuntimeDataset(await fetchMyTeamDataset(intent.identity.orgName));
      armPreview(intent.solutionCode, MY_TEAM_DATASET_CODE);
    } catch {
      clearIntent();
      return;
    }
  }

  if (!isPreviewArmed()) return;

  enablePreviewMock();

  queryClient.setQueryData(queryKeys.users.current(), buildUser(intent.identity));
  queryClient.setQueryData(queryKeys.organization.current(), buildOrg(intent.identity));
  queryClient.setQueryData(
    queryKeys.groups.list(),
    buildGroups(getDataset(getPreviewDatasetCode()))
  );
}
